import { ChevronDown, ChevronRight, LayoutGrid, Search } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type FC } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import {
  useCategoryPath,
  useShelfCategoryTree,
  type ShelfBranch,
} from '@/features/storefront/hooks/useShelfCategories';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  CHROME_QUIET_SCROLL,
  menuItems,
  type HeaderProps,
} from '@/features/storefront/layouts/chrome/types';
import { CategoryLinkIcon } from '@/features/storefront/layouts/chrome/CategoryLinkIcon';
import { HeaderLocationSelector } from '@/features/storefront/layouts/chrome/HeaderLocationSelector';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_MONO_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * The masthead's ground, and the one thing in this file worth explaining.
 *
 * The shopper's corner - search, account, basket - is built by the shell and
 * handed to every theme as `endSlot`, and it paints itself in the primary
 * colour so it reads as the shop's own. This theme's primary is a signal red
 * and this deck is hazard yellow, which is a pair no small text should ever be
 * set in. Rather than take the corner apart, the deck redefines the variable
 * the corner reads: inside the band the primary *is* the ink, so the corner
 * comes out black on yellow exactly as it is drawn. Nothing outside the band
 * is touched, and a merchant who repaints their red still repaints their
 * buttons - which is the property that would be lost by hardcoding a colour
 * into the corner instead.
 */
const MASTHEAD_STYLE = {
  backgroundColor: 'var(--storefront-accent)',
  '--storefront-primary': 'var(--storefront-text)',
} as CSSProperties;

/** The department bar: the row a trade catalogue is actually navigated from. */
const DEPARTMENT_NAV = cn(
  'flex h-12 items-center gap-7 overflow-x-auto whitespace-nowrap text-[12.5px] font-semibold uppercase tracking-[0.06em]',
  CHROME_QUIET_SCROLL,
);

/** The block that opens the department panel: the bar's one yellow thing. */
const DEPARTMENT_BLOCK =
  'flex h-12 shrink-0 items-center gap-2 text-[12.5px] font-bold uppercase tracking-[0.08em]';
const DEPARTMENT_BLOCK_STYLE = { color: 'var(--storefront-accent)' } as CSSProperties;

/**
 * The block as a label rather than a control: what the customiser preview
 * wears, and what a shop with no shelves to open falls back to.
 */
function DepartmentLabel({ label }: { label: string }) {
  return (
    <span className={DEPARTMENT_BLOCK} style={DEPARTMENT_BLOCK_STYLE}>
      <LayoutGrid className="size-4" aria-hidden />
      {label}
    </span>
  );
}

/**
 * How many departments the panel lists, and how many sub-departments a column
 * takes before the flyout starts a second one.
 */
const DEPARTMENT_LIMIT = 12;
const SUB_DEPARTMENT_COLUMN = 6;

/**
 * The counter's search box.
 *
 * A field rather than the shell's search *link*, because a hardware shop is
 * searched by part: somebody arrives knowing they want a 13mm keyless chuck,
 * and a trip through a landing page to find the box is a trip they should not
 * have to make. The address it navigates to is the search page's own, so the
 * term, the facets and the back button keep working exactly as they do when a
 * shopper gets there the shell's way.
 *
 * Only mounted outside the customiser preview - it reaches for the router, and
 * the preview renders this chrome with none around it.
 */
function SearchField() {
  const paths = useStorefrontPaths();
  const navigate = useNavigate();
  const copy = useShopContent();
  const [term, setTerm] = useState('');

  return (
    <form
      role="search"
      className="flex h-11 min-w-0 flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        const query = term.trim();
        if (query) navigate(paths.search(new URLSearchParams({ q: query })));
      }}
    >
      <input
        type="search"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        aria-label={copy('theme.search_catalogue')}
        placeholder={copy('theme.search_tools_placeholder')}
        className="h-full min-w-0 flex-1 border-0 px-4 text-sm outline-none"
        style={{ backgroundColor: 'var(--storefront-background)', color: 'var(--storefront-text)' }}
      />
      <button
        type="submit"
        aria-label={copy('header.search')}
        className="grid h-full w-12 shrink-0 place-items-center"
        style={{ backgroundColor: 'var(--storefront-palette-text, var(--storefront-text))', color: 'var(--storefront-palette-background, var(--storefront-background))' }}
      >
        <Search className="size-4" aria-hidden />
      </button>
    </form>
  );
}

/**
 * The department panel: the shop's shelves in one column, and whatever is
 * filed under the one being read in the sheet beside it.
 *
 * A trade catalogue is not navigated department by department. Somebody wants
 * an SDS-max hammer, and the useful question is not "which of these nine
 * shelves is that on" but "show me what is on the hammer shelf" - so the panel
 * answers both at once: the departments stand still on the left while the
 * shelf under the pointer opens on the right, with its own picture and a way
 * straight through to the whole department.
 *
 * Built from the shop's *categories* rather than the merchant's menu on
 * purpose. The menu is the shop's voice - its sale, its journal, its trade
 * counter - and the categories are its shelves; putting the shelves here is
 * what leaves the bar beside it free to carry the voice, and means the panel
 * is right the moment a merchant adds a shelf rather than the moment they
 * remember to add a link to it.
 *
 * Opens on hover for a pointer and on click or Enter for everything else, and
 * every row is a link either way: a department is reachable whether or not a
 * shopper ever waits for the sheet to open.
 */
function DepartmentMenu({ label, icons = false }: { label: string; icons?: boolean }) {
  const branches = useShelfCategoryTree({ limit: DEPARTMENT_LIMIT });
  const categoryPath = useCategoryPath();
  const location = useLocation();
  const copy = useShopContent();
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState<string | null>(null);

  // A followed link navigates underneath an open panel, so the panel has to
  // get out of the way by itself - nothing else closes it.
  useEffect(() => {
    setOpen(false);
    setReading(null);
  }, [location.pathname, location.search]);

  // A shop with no shelves has nothing to open, so the block stays the label
  // it has always been rather than becoming a button that does nothing.
  if (branches.length === 0) return <DepartmentLabel label={label} />;

  // The sheet opens on the first shelf that has anything under it, so a panel
  // that has just been opened is never a column beside an empty half.
  const openable = branches.filter((branch) => branch.children.length > 0);
  const branch =
    branches.find((entry) => entry.category.id === reading) ??
    (reading === null ? openable[0] : undefined);

  const close = () => {
    setOpen(false);
    setReading(null);
  };

  return (
    <div
      className="relative shrink-0"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') close();
      }}
    >
      {/* The one control on the bar that is not a department, so it is the one
          thing on it wearing the yellow. */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls="tools-machine-departments"
        onClick={() => (open ? close() : setOpen(true))}
        className={DEPARTMENT_BLOCK}
        style={DEPARTMENT_BLOCK_STYLE}
      >
        <LayoutGrid className="size-4" aria-hidden />
        {label}
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open ? (
        <div id="tools-machine-departments" className="absolute start-0 top-full z-40 flex shadow-2xl">
          <nav
            aria-label={copy('theme.departments')}
            className="w-64 shrink-0 py-2 text-sm"
            style={{
              backgroundColor: 'var(--storefront-palette-text, var(--storefront-text))',
              color: 'var(--storefront-palette-background, var(--storefront-background))',
            }}
          >
            {branches.map(({ category, children }) => {
              const active = branch?.category.id === category.id;
              return (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  onMouseEnter={() => setReading(category.id)}
                  onFocus={() => setReading(category.id)}
                  className="flex items-center justify-between gap-3 px-5 py-2.5 font-semibold"
                  style={
                    active
                      ? {
                          backgroundColor: 'var(--storefront-accent)',
                          color: 'var(--storefront-text)',
                        }
                      : undefined
                  }
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {icons ? <CategoryLinkIcon category={category} /> : null}
                    <span className="truncate">{category.name}</span>
                  </span>
                  {children.length > 0 ? (
                    <ChevronRight className="size-3.5 shrink-0 opacity-70 rtl-flip" aria-hidden />
                  ) : null}
                </Link>
              );
            })}
          </nav>

          {branch && branch.children.length > 0 ? (
            <SubDepartments key={branch.category.id} branch={branch} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One department, opened: what is filed under it, and the shelf itself.
 *
 * The picture is the department's own - the same one its card on the index
 * page wears - because a hardware shop is recognised by the tool rather than
 * the word, and a shopper who is one hover away from the wrong shelf should be
 * able to see that it is the wrong shelf.
 */
function SubDepartments({ branch }: { branch: ShelfBranch }) {
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const { category, children } = branch;
  const columns = Math.min(3, Math.ceil(children.length / SUB_DEPARTMENT_COLUMN) || 1);

  return (
    <div
      className="hidden min-w-[36rem] max-w-[58rem] gap-8 border p-7 lg:flex"
      style={{
        backgroundColor: 'var(--storefront-background)',
        color: 'var(--storefront-text)',
        borderColor: 'var(--storefront-surface)',
      }}
    >
      <div className="min-w-0 flex-1">
        <p
          className="mb-3 text-[11px] uppercase tracking-[0.14em]"
          style={{ ...STOREFRONT_MONO_STYLE, color: 'var(--storefront-muted)' }}
        >
          {category.name}
        </p>
        <div
          className="grid gap-x-8 gap-y-1"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(12rem, 1fr))` }}
        >
          {children.map((child) => (
            <Link
              key={child.id}
              to={categoryPath(child.slug)}
              className="truncate py-1.5 text-sm hover:opacity-70"
              title={child.name}
            >
              {child.name}
            </Link>
          ))}
        </div>
        <Link
          to={categoryPath(category.slug)}
          className="mt-5 inline-flex h-9 items-center px-4 text-[12px] font-bold uppercase tracking-[0.08em]"
          style={STOREFRONT_BUTTON_STYLE}
        >
          {copy('theme.shop_all', { name: category.name })}
        </Link>
      </div>

      {category.image_url ? (
        // Clips, so a zoomed picture stays inside the panel's column.
        <Link to={categoryPath(category.slug)} className="hidden w-48 shrink-0 overflow-hidden xl:block">
          <StorefrontImage
            url={category.image_url}
            sizes={fixedImageSizes(192)}
            alt=""
            className="h-full w-full object-cover"
            style={focalStyle(category.image_focal)}
          />
        </Link>
      ) : null}
    </div>
  );
}

/**
 * A trade counter's masthead, in three decks.
 *
 * The rail carries the standing promises a hardware shop leads with - warranty
 * and next-day delivery are half of why somebody buys a drill from one shop
 * rather than another. The yellow deck carries the wordmark, the search box
 * and the shopper's corner. The black deck under it carries the departments,
 * which need a row of their own: a shop with nine of them cannot put them
 * beside a wordmark, and the departments are the way into this catalogue.
 *
 * The departments themselves live in the panel the black deck opens - shelf
 * then sub-shelf, which is how a hardware catalogue is actually arranged - and
 * the row beside it carries the merchant's menu.
 *
 * Like Serra and Voltic, the bar stands the shop's own shelves in when the
 * merchant has built no menu - a hardware header with nothing in it is not a
 * quieter header, it is a shop with no way to the tools.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);
  const copy = useShopContent();
  // With no menu built the bar stands the shelves in, and the shelves query is
  // skipped in preview - so in preview an empty menu means no bar at all.
  const hasBar = items.length > 0 || !preview;

  return (
    <header
      className={cn(header.sticky && !preview && 'sticky top-0 z-40')}
      style={{ backgroundColor: 'var(--storefront-background)' }}
    >
      <HeaderBannerRail header={header} fallback={description} preview={preview} />

      <div style={MASTHEAD_STYLE}>
        {/* Tighter on a phone, where the hamburger, the wordmark and the
            corner share 340-odd pixels and every gap is taken out of the name. */}
        <StorefrontContainer className="flex min-h-[4.75rem] items-center gap-3 py-3 sm:gap-5 preview-phone:gap-3!">
          <MobileNav menu={menu} hiddenFrom="md:hidden" preview={preview} categoryTree />
          <StoreLogo
            storeName={storeName}
            logoUrl={logoUrl}
            logoWidthPx={header.logo_width_px}
            showStoreName={header.show_store_name}
            preview={preview}
            className="gap-2.5 sm:gap-3"
            nameClassName="text-xl font-bold uppercase tracking-[0.02em] sm:text-[1.75rem] sm:tracking-[0.04em] preview-phone:text-xl! preview-phone:tracking-[0.02em]!"
            nameStyle={{ fontFamily: 'var(--storefront-heading-font)' }}
          />
          <HeaderLocationSelector header={header} preview={preview} placement="inline" />
          {/* The box takes the middle of the deck from the large breakpoint up
              and stands down below it, where the wordmark and the corner have
              the row to themselves and the sheet carries the navigation. */}
          <div className="hidden min-w-0 flex-1 justify-center lg:flex preview-narrow:hidden!">
            {preview ? (
              <div
                aria-hidden
                className="flex h-11 min-w-0 flex-1 items-center px-4 text-sm"
                style={{
                  backgroundColor: 'var(--storefront-background)',
                  color: 'var(--storefront-muted)',
                }}
              >
                {copy('theme.search_tools_placeholder')}
              </div>
            ) : (
              <SearchField />
            )}
          </div>
          <div className="ms-auto flex shrink-0 items-center gap-4 sm:gap-5 preview-phone:gap-4!">
            {endSlot}
          </div>
        </StorefrontContainer>
      </div>

      {hasBar ? (
        <div
          className="hidden md:block preview-phone:hidden!"
          style={{
            backgroundColor: 'var(--storefront-palette-text, var(--storefront-text))',
            color: 'var(--storefront-palette-background, var(--storefront-background))',
          }}
        >
          <StorefrontContainer className="flex items-center gap-7">
            {/* Inert in the customiser preview, which renders this chrome with
                no shop around it to ask for shelves. */}
            {preview ? (
              <DepartmentLabel label={header.department_menu_label} />
            ) : (
              <DepartmentMenu label={header.department_menu_label} icons={header.category_icons} />
            )}
            {items.length > 0 ? (
              <nav aria-label={menu?.name} className={DEPARTMENT_NAV}>
                {items.map((item) => (
                  <MenuLink
                    key={`${item.label}:${item.url}`}
                    item={item}
                    preview={preview}
                    className="hover:opacity-70"
                  />
                ))}
              </nav>
            ) : (
              /* Eight, because this bar is a row of its own with a wide page
                 column to spend and a hardware shop has the departments to
                 fill it. */
              <HeaderCategoryLinks
                className={DEPARTMENT_NAV}
                linkClassName="hover:opacity-70"
                header={header}
                limit={8}
              />
            )}
            {/* The counter's own line, in the face this theme sets its numbers
                in: the last thing read before the catalogue starts. Empty
                hides it rather than rendering blank chrome. */}
            {header.department_bar_tagline ? (
              <span
                className="ms-auto hidden shrink-0 text-[11px] uppercase tracking-[0.1em] opacity-70 xl:block"
                style={STOREFRONT_MONO_STYLE}
              >
                {header.department_bar_tagline}
              </span>
            ) : null}
          </StorefrontContainer>
        </div>
      ) : null}
    </header>
  );
};
